import { Button, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import Icon from 'components/Icon';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { navigateToLockedContent } from 'features/layout/features/content/actions';
import { useTranslation } from 'react-i18next';
import {
  selectCurrentContentId,
  selectLockedContent,
} from 'features/layout/features/content/contentSlice';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';

const Root = styled(Button)(({ theme }) => ({
  position: 'absolute',
  left: 10,
  zIndex: 1000,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  padding: '0 12px 0 8px',
  fontSize: 14,
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  height: 34,
  gap: theme.spacing(1),
  borderRadius: '20px',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const CompactRoot = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  left: 10,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

type NavigateToLockedContentButtonProps = {
  compact?: boolean;
};

export const NavigateToLockedContentButton = ({ compact }: NavigateToLockedContentButtonProps) => {
  const { t } = useTranslation('room');
  const dispatch = useAppDispatch();
  const { hasPermissions } = usePermissions();

  const canLockContent = hasPermissions(PermissionTypes.lockContent);
  const lockedContent = useAppSelector(selectLockedContent);
  const currentContentId = useAppSelector(selectCurrentContentId);

  const isFocusedOnLockedContent = lockedContent?.id === currentContentId;

  if (!lockedContent || !canLockContent || isFocusedOnLockedContent) {
    return null;
  }

  const handleClick = () => {
    dispatch(navigateToLockedContent());
  };

  if (compact) {
    return (
      <Tooltip title={t('grid.go_to_locked_content')}>
        <CompactRoot onClick={handleClick} size="small">
          <Icon name="arrow-lock-box" sx={{ fontSize: 26 }} />
        </CompactRoot>
      </Tooltip>
    );
  }

  return (
    <Root onClick={handleClick} variant="contained">
      <Icon
        name="arrow-lock-box"
        sx={{
          fontSize: 30,
        }}
      />
      {t('grid.go_to_locked_content')}
    </Root>
  );
};
